import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, X, Plus, Pin, ExternalLink, Sparkles, AlertTriangle, Info, CheckCircle2, Newspaper, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_META = {
  info: { label: 'Info', Icon: Info, badge: 'bg-sky-100 text-sky-700', border: 'border-sky-200', bg: 'bg-sky-50' },
  success: { label: 'Positivo', Icon: CheckCircle2, badge: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50' },
  warning: { label: 'Attenzione', Icon: AlertTriangle, badge: 'bg-amber-100 text-amber-700', border: 'border-amber-200', bg: 'bg-amber-50' },
  news: { label: 'Notizia', Icon: Newspaper, badge: 'bg-indigo-100 text-indigo-700', border: 'border-indigo-200', bg: 'bg-indigo-50' },
  aggiornamento: { label: 'Aggiornamento', Icon: Sparkles, badge: 'bg-fuchsia-100 text-fuchsia-700', border: 'border-fuchsia-200', bg: 'bg-fuchsia-50' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const AnnouncementsBoard = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState({ titolo: '', messaggio: '', tipo: 'info', pinned: false, link: '', scadenza: '' });
  const [submitting, setSubmitting] = useState(false);

  const isEditor = user?.ruolo === 'super_admin' || user?.ruolo === 'interno';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard-news`);
      setItems(res.data || []);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.titolo.trim() || !form.messaggio.trim()) {
      toast.error('Titolo e messaggio obbligatori');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/dashboard-news`, {
        ...form,
        link: form.link.trim() || null,
        scadenza: form.scadenza || null,
      });
      toast.success('Annuncio pubblicato');
      setForm({ titolo: '', messaggio: '', tipo: 'info', pinned: false, link: '', scadenza: '' });
      setShowEditor(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore pubblicazione');
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = async (id) => {
    try {
      await axios.post(`${API_URL}/api/dashboard-news/${id}/dismiss`);
      setItems(prev => prev.filter(a => a.id !== id));
    } catch (_) { toast.error('Impossibile scartare'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminare definitivamente questo annuncio?')) return;
    try {
      await axios.delete(`${API_URL}/api/dashboard-news/${id}`);
      setItems(prev => prev.filter(a => a.id !== id));
      toast.success('Annuncio eliminato');
    } catch (_) { toast.error('Errore eliminazione'); }
  };

  if (loading) return null;
  if (items.length === 0 && !isEditor) return null;

  return (
    <Card className="border-slate-200" data-testid="announcements-board">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <Megaphone className="w-4 h-4 text-indigo-600" />
            In primo piano
            {items.length > 0 && <Badge variant="outline" className="text-[10px]">{items.length}</Badge>}
          </CardTitle>
          {isEditor && (
            <Button
              size="sm"
              variant={showEditor ? 'outline' : 'default'}
              onClick={() => setShowEditor(s => !s)}
              data-testid="announce-new-btn"
              className="h-8"
            >
              {showEditor ? <X className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              {showEditor ? 'Chiudi' : 'Nuovo annuncio'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {showEditor && isEditor && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-3" data-testid="announce-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Titolo</Label>
                <Input value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} placeholder="Es. Nuova release CRM v2.5" data-testid="announce-title" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Messaggio</Label>
                <Textarea rows={3} value={form.messaggio} onChange={e => setForm(f => ({ ...f, messaggio: e.target.value }))} placeholder="Descrivi la novita' o l'aggiornamento in poche righe..." data-testid="announce-message" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger className="h-9" data-testid="announce-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPO_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Scadenza (opzionale)</Label>
                <Input type="date" value={form.scadenza} onChange={e => setForm(f => ({ ...f, scadenza: e.target.value }))} data-testid="announce-scadenza" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Link approfondimento (opzionale)</Label>
                <Input value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="https:// oppure /companies" data-testid="announce-link" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.pinned} onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))} data-testid="announce-pinned" />
                <Pin className="w-3.5 h-3.5" /> Fissa in alto
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowEditor(false)}>Annulla</Button>
              <Button size="sm" onClick={submit} disabled={submitting} data-testid="announce-submit">
                {submitting ? 'Pubblico...' : 'Pubblica'}
              </Button>
            </div>
          </div>
        )}

        {items.length === 0 && !showEditor && isEditor && (
          <p className="text-sm text-slate-500 py-4 text-center">Nessun annuncio attivo. Clicca "Nuovo annuncio" per crearne uno.</p>
        )}

        {items.map(a => {
          const meta = TIPO_META[a.tipo] || TIPO_META.info;
          const Icon = meta.Icon;
          const linkIsExternal = a.link && /^https?:/i.test(a.link);
          return (
            <div
              key={a.id}
              className={`rounded-lg border ${meta.border} ${meta.bg} p-3 relative`}
              data-testid={`announce-item-${a.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${meta.badge}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900">{a.titolo}</p>
                      {a.pinned && <Pin className="w-3 h-3 text-slate-500" />}
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.badge}`}>{meta.label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-500">{formatDate(a.created_at)}</span>
                      {isEditor && (
                        <button
                          onClick={() => remove(a.id)}
                          className="p-1 text-slate-400 hover:text-red-500 rounded"
                          title="Elimina definitivamente"
                          data-testid={`announce-delete-${a.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => dismiss(a.id)}
                        className="p-1 text-slate-400 hover:text-slate-700 rounded"
                        title="Segna come letto"
                        data-testid={`announce-dismiss-${a.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{a.messaggio}</p>
                  {a.link && (
                    <a
                      href={a.link}
                      target={linkIsExternal ? '_blank' : undefined}
                      rel={linkIsExternal ? 'noreferrer' : undefined}
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 mt-2 font-medium"
                    >
                      Scopri di piu' <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {a.autore_nome && (
                    <p className="text-[10px] text-slate-400 mt-2">Pubblicato da {a.autore_nome}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AnnouncementsBoard;
